import React, { useState } from 'react';
import { useMyProperties } from '../../hooks/useProperties';
import { MediaUpload } from '../Media/MediaUpload';
import { TipoImovel } from '../../types/Property';
import './PropertyForm.css';

interface PropertyFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const PropertyForm: React.FC<PropertyFormProps> = ({ onSuccess, onCancel }) => {
  const { createProperty } = useMyProperties();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdImovelId, setCreatedImovelId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    tipo_imovel: 'apartamento' as TipoImovel,
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    bairro: '',
    quartos: 1,
    banheiros: 1,
    vagas_garagem: 0,
    area_util: 0,
    valor_aluguel: 0,
    valor_condominio: 0,
    valor_iptu: 0,
    aceita_pet: false,
    mobiliado: false,
    ar_condicionado: false,
    internet: false,
    comodidades: [] as string[],
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : type === 'number'
        ? parseFloat(value) || 0
        : value,
    }));
  };

  const handleComodidadeChange = (comodidade: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      comodidades: checked
        ? [...prev.comodidades, comodidade]
        : prev.comodidades.filter(c => c !== comodidade),
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.titulo.trim()) return 'Título é obrigatório';
    if (!formData.descricao.trim()) return 'Descrição é obrigatória';
    if (!formData.endereco.trim()) return 'Endereço é obrigatório';
    if (!formData.cidade.trim()) return 'Cidade é obrigatória';
    if (!formData.estado.trim()) return 'Estado é obrigatório';
    if (!formData.cep.trim()) return 'CEP é obrigatório';
    const isGalpao = formData.tipo_imovel === TipoImovel.GALPAO;
    const isComercial = formData.tipo_imovel === TipoImovel.COMERCIAL;
    if (!isGalpao && !isComercial && formData.quartos < 1) return 'Número de quartos deve ser pelo menos 1';
    if (formData.banheiros < 1) return 'Número de banheiros deve ser pelo menos 1';
    if (formData.area_util <= 0) return 'Área útil deve ser maior que 0';
    if (formData.valor_aluguel <= 0) return 'Valor do aluguel deve ser maior que 0';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const result = await createProperty(formData);
      
      if (result.success && result.data && result.data.id) {
        setSuccess(true);
        setCreatedImovelId(result.data.id);
        onSuccess?.();
      } else {
        setError(result.message || 'Erro ao criar imóvel');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao criar imóvel');
    } finally {
      setLoading(false);
    }
  };

  const comodidadesOptions = [
    'piscina',
    'academia',
    'portaria 24h',
    'elevador',
    'churrasqueira',
    'playground',
    'salão de festas',
    'espaço gourmet',
    'quadra',
    'lavanderia',
    'piscina adulto',
    'piscina infantil',
    'sauna',
    'cinema',
    'biblioteca',
  ];

  if (success) {
    return (
      <div className="property-form">
        <div className="success-message">
          <div className="success-icon">
            
          </div>
          <h2>Imóvel Criado com Sucesso!</h2>
          <p>Seu imóvel foi criado e está aguardando aprovação.</p>
          <p>Status: <span className="status-pendente">Pendente</span></p>
          
          {createdImovelId && (
            <div className="upload-section">
              <h3>Adicionar Fotos</h3>
              <p>Adicione fotos do seu imóvel para melhorar a visibilidade:</p>
              <MediaUpload
                imovelId={createdImovelId}
                onUploadSuccess={() => {
                  console.log('Fotos enviadas com sucesso!');
                }}
              />
            </div>
          )}
          
          <div className="form-actions">
            <button onClick={onCancel} className="btn-secondary">
              Voltar
            </button>
            <button onClick={() => window.location.reload()} className="btn-primary">
              Criar Novo Imóvel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="property-form">
      <div className="form-header">
        <h2>Adicionar Novo Imóvel</h2>
        <p>Preencha as informações do seu imóvel</p>
      </div>

      {error && (
        <div className="form-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="form-content">
        {/* Informações Básicas */}
        <div className="form-section">
          <h3>Informações Básicas</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="titulo">Título do Imóvel *</label>
              <input
                type="text"
                id="titulo"
                name="titulo"
                value={formData.titulo}
                onChange={handleInputChange}
                placeholder="Ex: Apartamento 2 quartos no centro"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="tipo_imovel">Tipo do Imóvel *</label>
              <select
                id="tipo_imovel"
                name="tipo_imovel"
                value={formData.tipo_imovel}
                onChange={handleInputChange}
                required
                disabled={loading}
              >
                <option value="apartamento">Apartamento</option>
                <option value="casa">Casa</option>
                <option value="kitnet">Kitnet</option>
                <option value="studio">Studio</option>
                <option value="loft">Loft</option>
                <option value="duplex">Duplex</option>
                <option value="cobertura">Cobertura</option>
                <option value="terreno">Terreno</option>
                <option value="comercial">Comercial</option>
                <option value="galpao">Galpão</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="descricao">Descrição *</label>
            <textarea
              id="descricao"
              name="descricao"
              value={formData.descricao}
              onChange={handleInputChange}
              placeholder="Descreva o imóvel, localização, comodidades..."
              rows={4}
              required
              disabled={loading}
            />
          </div>
        </div>

        {/* Localização */}
        <div className="form-section">
          <h3>Localização</h3>
          
          <div className="form-group">
            <label htmlFor="endereco">Endereço Completo *</label>
            <input
              type="text"
              id="endereco"
              name="endereco"
              value={formData.endereco}
              onChange={handleInputChange}
              placeholder="Rua, número, complemento"
              required
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="bairro">Bairro</label>
              <input
                type="text"
                id="bairro"
                name="bairro"
                value={formData.bairro}
                onChange={handleInputChange}
                placeholder="Nome do bairro"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="cidade">Cidade *</label>
              <input
                type="text"
                id="cidade"
                name="cidade"
                value={formData.cidade}
                onChange={handleInputChange}
                placeholder="Nome da cidade"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="estado">Estado *</label>
              <input
                type="text"
                id="estado"
                name="estado"
                value={formData.estado}
                onChange={handleInputChange}
                placeholder="UF"
                maxLength={2}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="cep">CEP *</label>
              <input
                type="text"
                id="cep"
                name="cep"
                value={formData.cep}
                onChange={handleInputChange}
                placeholder="00000-000"
                pattern="[0-9]{5}-[0-9]{3}"
                required
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Características */}
        <div className="form-section">
          <h3>Características</h3>
          
          <div className="form-row">
            {formData.tipo_imovel !== TipoImovel.COMERCIAL && formData.tipo_imovel !== TipoImovel.GALPAO && (
              <div className="form-group">
                <label htmlFor="quartos">Quartos *</label>
                <input
                  type="number"
                  id="quartos"
                  name="quartos"
                  value={formData.quartos}
                  onChange={handleInputChange}
                  min="1"
                  required
                  disabled={loading}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="banheiros">Banheiros *</label>
              <input
                type="number"
                id="banheiros"
                name="banheiros"
                value={formData.banheiros}
                onChange={handleInputChange}
                min="1"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="vagas_garagem">Vagas de Garagem</label>
              <input
                type="number"
                id="vagas_garagem"
                name="vagas_garagem"
                value={formData.vagas_garagem}
                onChange={handleInputChange}
                min="0"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="area_util">Área Útil (m²) *</label>
              <input
                type="number"
                id="area_util"
                name="area_util"
                value={formData.area_util}
                onChange={handleInputChange}
                min="1"
                required
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Valores */}
        <div className="form-section">
          <h3>Valores</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="valor_aluguel">Valor do Aluguel (R$) *</label>
              <input
                type="number"
                id="valor_aluguel"
                name="valor_aluguel"
                value={formData.valor_aluguel}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="valor_condominio">Condomínio (R$)</label>
              <input
                type="number"
                id="valor_condominio"
                name="valor_condominio"
                value={formData.valor_condominio}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="valor_iptu">IPTU (R$)</label>
              <input
                type="number"
                id="valor_iptu"
                name="valor_iptu"
                value={formData.valor_iptu}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Características Especiais */}
        <div className="form-section">
          <h3>Características Especiais</h3>
          
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="aceita_pet"
                checked={formData.aceita_pet}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className="checkbox-text">Aceita Pet</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                name="mobiliado"
                checked={formData.mobiliado}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className="checkbox-text">Mobiliado</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                name="ar_condicionado"
                checked={formData.ar_condicionado}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className="checkbox-text">Ar Condicionado</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                name="internet"
                checked={formData.internet}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className="checkbox-text">Internet</span>
            </label>
          </div>
        </div>

        {/* Comodidades */}
        <div className="form-section">
          <h3>Comodidades do Condomínio</h3>
          
          <div className="comodidades-grid">
            {comodidadesOptions.map(comodidade => (
              <label key={comodidade} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.comodidades.includes(comodidade)}
                  onChange={(e) => handleComodidadeChange(comodidade, e.target.checked)}
                  disabled={loading}
                />
                <span className="checkbox-text">{comodidade}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Ações do Formulário */}
        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Criando...' : 'Criar Imóvel'}
          </button>
        </div>
      </form>
    </div>
  );
};
